import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useStore } from '../state/store';
import {
  dropEmptyFeeEarners,
  feeEarnersMissingCode,
  settingsEqual,
} from '@shared/settingsDirty.js';
import type { FeeEarnerEntry, SettingsSnapshot, TranscriptionProviderId } from '@shared/types.js';

// Aliases live in the model as string[], but the user types them as a
// single comma-separated string. The naive controlled-input pattern
// (value=arr.join, onChange=value.split.trim.filter) eats spaces and
// commas as the user types: typing "Ruth Winter" -> the trailing space
// is trimmed away on every keystroke and the cursor jumps.
//
// Keep a local "raw text" state and only canonicalise into the array
// on blur, so all keystrokes are preserved while editing.
function AliasesInput({
  aliases,
  onCommit,
}: {
  aliases: string[];
  onCommit: (next: string[]) => void;
}) {
  const canonical = aliases.join(', ');
  const [text, setText] = useState(canonical);
  useEffect(() => {
    setText(canonical);
  }, [canonical]);
  return (
    <input
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const next = text
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        onCommit(next);
        setText(next.join(', '));
      }}
    />
  );
}

export function Settings() {
  const { settings, loadSettings, saveSettings, isAdmin, toast } = useStore((s) => ({
    settings: s.settings,
    loadSettings: s.loadSettings,
    saveSettings: s.saveSettings,
    isAdmin: s.authUser?.role === 'admin',
    toast: s.toast,
  }));

  const [draft, setDraft] = useState<SettingsSnapshot | null>(null);

  useEffect(() => {
    if (!settings) void loadSettings();
    else if (!draft) setDraft(settings);
  }, [settings, draft, loadSettings]);

  // Edits live in `draft` until "Save settings" is pressed. Publish that
  // dirty state so navigation can warn instead of silently discarding
  // work (a fee earner was lost this way on 2026-08-05).
  const dirty = Boolean(draft && settings && !settingsEqual(draft, settings));

  useEffect(() => {
    useStore.setState({ settingsDirty: dirty });
    return () => useStore.setState({ settingsDirty: false });
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  if (!draft) return <div className="loading">Loading settings…</div>;

  async function onSave() {
    // An accidental "+ Add fee earner" leaves a blank row; drop those,
    // but never silently bin a row someone half-filled in.
    const cleaned: SettingsSnapshot = {
      ...draft!,
      feeEarners: dropEmptyFeeEarners(draft!.feeEarners),
    };
    const missing = feeEarnersMissingCode(cleaned.feeEarners);
    if (missing.length > 0) {
      const who = missing
        .map((m) => m.displayName || m.aliases.join(' / ') || 'a new fee earner')
        .join(', ');
      toast('warn', `Add a code (e.g. initials) for ${who} before saving.`);
      return;
    }
    setDraft(cleaned);
    await saveSettings(cleaned);
  }

  function update<K extends keyof SettingsSnapshot>(k: K, v: SettingsSnapshot[K]) {
    setDraft({ ...draft!, [k]: v });
  }

  function updateFeeEarner(idx: number, fe: FeeEarnerEntry) {
    const next = [...draft!.feeEarners];
    next[idx] = fe;
    update('feeEarners', next);
  }
  function addFeeEarner() {
    update('feeEarners', [...draft!.feeEarners, { code: '', aliases: [] }]);
  }
  function removeFeeEarner(idx: number) {
    update('feeEarners', draft!.feeEarners.filter((_, i) => i !== idx));
  }

  return (
    <div className="settings">
      <h2>Settings</h2>
      {!isAdmin && (
        <div className="settings-readonly">
          Settings are firm-wide and editable by admins only. You're viewing them in read-only mode.
        </div>
      )}

      <fieldset disabled={!isAdmin} className="settings-fieldset">
      <section>
        <h3>Fee earners</h3>
        <p className="muted">
          Codes used in the export, plus aliases for "fee earner John" → JH.
          {isAdmin && ' New fee earners are not live until you press "Save settings" at the bottom of this page.'}
        </p>
        <table className="fee-earners">
          <thead><tr><th>Code</th><th>Aliases (comma-separated)</th><th>Display name</th><th></th></tr></thead>
          <tbody>
            {draft.feeEarners.map((fe, i) => (
              <tr key={i}>
                <td>
                  <input
                    value={fe.code}
                    placeholder="e.g. SB"
                    onChange={(e) => updateFeeEarner(i, { ...fe, code: e.target.value })}
                    style={
                      !fe.code.trim() &&
                      (fe.aliases.some((a) => a.trim()) || (fe.displayName ?? '').trim())
                        ? { borderColor: '#c84' }
                        : undefined
                    }
                  />
                </td>
                <td>
                  <AliasesInput
                    aliases={fe.aliases}
                    onCommit={(next) => updateFeeEarner(i, { ...fe, aliases: next })}
                  />
                </td>
                <td><input value={fe.displayName ?? ''} onChange={(e) => updateFeeEarner(i, { ...fe, displayName: e.target.value || undefined })} /></td>
                <td><button onClick={() => removeFeeEarner(i)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={addFeeEarner}>+ Add fee earner</button>
      </section>

      <section>
        <h3>Time and units</h3>
        <label>
          One unit equals (decimal time):
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={draft.unitToDecimal}
            onChange={(e) => update('unitToDecimal', Number(e.target.value))}
          />
        </label>
        <label>
          High-units warning threshold:
          <input
            type="number"
            min="1"
            value={draft.highUnitWarningThreshold}
            onChange={(e) => update('highUnitWarningThreshold', Number(e.target.value))}
          />
        </label>
      </section>

      <section>
        <h3>Display & export</h3>
        <label>
          Export date format:
          <select value={draft.exportDateFormat} onChange={(e) => update('exportDateFormat', e.target.value as SettingsSnapshot['exportDateFormat'])}>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </label>
        <label>
          <input type="checkbox" checked={draft.hideVoidedRowsByDefault} onChange={(e) => update('hideVoidedRowsByDefault', e.target.checked)} />
          Hide voided rows by default
        </label>
        <label>
          <input type="checkbox" checked={draft.allowUnknownFeeEarners} onChange={(e) => update('allowUnknownFeeEarners', e.target.checked)} />
          Allow unknown fee earners (warn but do not block)
        </label>
      </section>

      <section>
        <h3>Suspiciously short descriptions</h3>
        <p className="muted">Comma-separated. Rows whose description matches one of these are flagged.</p>
        <input
          value={draft.suspiciouslyShortDescriptions.join(', ')}
          onChange={(e) => update('suspiciouslyShortDescriptions', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
        />
      </section>

      <TranscriptionSettings draft={draft} update={update} />
      </fieldset>

      {isAdmin && (
        <div
          className="settings-actions"
          style={{
            position: 'sticky',
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 12px',
            marginTop: 12,
            borderTop: '1px solid #888',
            background: dirty ? '#c8441a14' : 'transparent',
          }}
        >
          <button
            onClick={() => void onSave()}
            style={dirty ? { fontWeight: 700 } : undefined}
          >
            Save settings
          </button>
          <button onClick={() => setDraft(settings)} disabled={!dirty}>Revert</button>
          <span style={{ fontSize: 13, opacity: 0.85 }}>
            {dirty ? '● Unsaved changes — press Save settings to apply them' : 'All changes saved'}
          </span>
        </div>
      )}
    </div>
  );
}

interface TxProps {
  draft: SettingsSnapshot;
  update: <K extends keyof SettingsSnapshot>(k: K, v: SettingsSnapshot[K]) => void;
}

function TranscriptionSettings({ draft, update }: TxProps) {
  const [openaiConfigured, setOpenaiConfigured] = useState<boolean>(false);

  useEffect(() => {
    void api.transcriptionHasKey('openai').then(setOpenaiConfigured);
  }, []);

  return (
    <section>
      <h3>Transcription provider</h3>
      <p className="muted">
        Manual mode is fully local. Cloud providers ship audio off the
        machine and require explicit acknowledgement of the privacy notice
        before they can be enabled. The firm's OpenAI key is configured
        on the server by an administrator; users never see or enter it.
      </p>

      <label>
        Provider:
        <select
          value={draft.transcriptionProvider}
          onChange={(e) => update('transcriptionProvider', e.target.value as TranscriptionProviderId)}
        >
          <option value="manual">Manual (local-only)</option>
          <option value="openai">OpenAI Whisper</option>
        </select>
      </label>

      <div className="privacy-warning">
        <strong>Privacy notice (record of decision):</strong> Audio and transcripts
        are sent to the configured transcription provider for processing. The
        firm has acknowledged this by enabling a cloud provider above. Switch
        Provider back to "Manual" to keep all audio local.
      </div>
      <label>
        <input
          type="checkbox"
          checked={draft.cloudPrivacyAcknowledged}
          onChange={(e) => update('cloudPrivacyAcknowledged', e.target.checked)}
        />
        Firm has acknowledged the privacy notice
      </label>

      {draft.transcriptionProvider === 'openai' && (
        <div className="provider-config">
          <h4>OpenAI configuration</h4>
          <div className={`muted ${openaiConfigured ? 'ok' : 'warn'}`}>
            Server-side API key: <strong>{openaiConfigured ? 'configured' : 'not configured'}</strong>.
            {' '}Rotation is via the server administrator.
          </div>

          <label>
            Live text speed:
            <select
              value={draft.streamingDelay}
              onChange={(e) =>
                update('streamingDelay', e.target.value as SettingsSnapshot['streamingDelay'])
              }
            >
              <option value="minimal">Fastest</option>
              <option value="low">Balanced (recommended)</option>
              <option value="medium">Most accurate</option>
            </select>
          </label>

          <details className="provider-advanced">
            <summary>Advanced</summary>
            <label>
              Batch/import model:
              <input
                value={draft.openaiModel}
                onChange={(e) => update('openaiModel', e.target.value)}
              />
            </label>
            <label>
              OpenAI base URL:
              <input
                value={draft.openaiBaseUrl}
                onChange={(e) => update('openaiBaseUrl', e.target.value)}
              />
            </label>
          </details>
        </div>
      )}
    </section>
  );
}
